/**
 * @see {@link https://tailwindui.com/components/application-ui/lists/feeds|TailwindUI Feeds}
 */
import { toast } from 'react-toastify';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { isBefore, startOfDay, subDays } from 'date-fns';
import { useBoolean } from 'usehooks-ts';

import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from '@/components/ui/dropdown';
import { useMe } from '@/hooks/use-me';
import { useNow } from '@/hooks/useNow';
import { formatDate, formatDistance } from '@/utils/format/date';

import type { TaskComment } from '../../../entities/task-comment';

import { CommentForm, type TaskCommentUpsertForm } from './task-comments-form';

export type TaskCommentsProps = {
  comments: TaskComment[];
  onCreate: (input: TaskCommentUpsertForm) => Promise<void>;
  onUpdate: (input: { id: string } & TaskCommentUpsertForm) => Promise<void>;
  onDelete: (input: { id: string }) => Promise<void>;
};

export function TaskComments({
  comments,
  onCreate,
  onDelete,
  onUpdate,
}: TaskCommentsProps) {
  const me = useMe();

  return (
    <div>
      <h3 className="mb-2 block text-sm font-medium leading-6 text-gray-900">
        Comments
      </h3>
      <ul className="space-y-3">
        {comments.map((comment) => (
          <li key={comment.id} className="relative flex gap-x-4">
            <CommentItem
              onDelete={onDelete}
              onUpdate={onUpdate}
              comment={comment}
              canEdit={me.email === comment.user.email}
            />
          </li>
        ))}
      </ul>

      <div className="mt-6">
        <CommentForm onSubmit={onCreate} />
      </div>
    </div>
  );
}

type CommentItemProps = {
  comment: TaskComment;
  canEdit?: boolean;
} & Pick<TaskCommentsProps, 'onUpdate' | 'onDelete'>;

function CommentItem({
  comment,
  canEdit,
  onUpdate,
  onDelete,
}: CommentItemProps) {
  const now = useNow(60_000);
  const weekAgo = startOfDay(subDays(now, 7));
  const isEdit = useBoolean(false);
  const isDeleting = useBoolean(false);

  if (isEdit.value) {
    return (
      <div className="w-full">
        <CommentForm
          comment={comment}
          onClose={() => isEdit.setFalse()}
          onSubmit={async (data) => {
            try {
              await onUpdate({ id: comment.id, ...data });
              isEdit.setFalse();
            } catch (error) {
              toast.error('Failed to update comment. Please try again.');
              throw error;
            }
          }}
        />
      </div>
    );
  }

  return (
    <>
      <div className="flex-auto rounded-md p-3 ring-1 ring-inset ring-gray-200">
        <div className="flex justify-between gap-x-4">
          <div className="flex gap-1 py-0.5 text-xs leading-5 text-gray-500">
            <span className="font-medium text-gray-900">
              {comment.user.name}
            </span>
            <time dateTime={comment.createdAt.toISOString()}>
              {isBefore(comment.createdAt, weekAgo)
                ? formatDate(comment.createdAt)
                : formatDistance(now, comment.createdAt)}
            </time>
          </div>
          {canEdit ? (
            <div>
              <Dropdown>
                <DropdownButton variant="ghost" className="p-1 text-zinc-600">
                  <ChevronDownIcon className="size-4" />
                </DropdownButton>
                <DropdownMenu className="absolute right-0 z-50">
                  <DropdownItem onClick={() => isEdit.setTrue()}>
                    Edit
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => isDeleting.setTrue()}
                    className="text-red-600 ui-active:bg-red-50"
                  >
                    Delete&hellip;
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>
          ) : null}
        </div>
        <p className="whitespace-pre-wrap text-sm leading-6 text-gray-500">
          {comment.content}
        </p>
        {comment.modified ? (
          <div className="text-xs leading-6 text-gray-400">(edited)</div>
        ) : null}
      </div>
      <ConfirmDialog
        className="mt-24"
        open={isDeleting.value}
        onClose={() => isDeleting.setFalse()}
        onSubmit={async function () {
          try {
            await onDelete(comment);
            isDeleting.setFalse();
          } catch (error) {
            toast.error('Failed to delete comment. Please try again.');
            throw error;
          }
        }}
        submitText="Delete"
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogDescription>
          Do you really want to delete this comment? This cannot be undone.
        </DialogDescription>
        <DialogBody>
          <div className="mx-4 rounded border border-zinc-300 p-3 text-sm leading-6 text-gray-500">
            <div className="flex gap-1 py-0.5 text-xs leading-5 text-gray-500">
              <span className="font-medium text-gray-900">
                {comment.user.name}
              </span>
              <time dateTime={comment.createdAt.toISOString()}>
                {isBefore(comment.createdAt, weekAgo)
                  ? formatDate(comment.createdAt)
                  : formatDistance(now, comment.createdAt)}
              </time>
            </div>
            <p className="whitespace-pre-wrap">{comment.content}</p>
          </div>
        </DialogBody>
      </ConfirmDialog>
    </>
  );
}
